import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import { updateQueryStringParam } from '@snapbrains/snaputils/src/query_string';

import SearchStyles from './styles/search_styles';
import ResultStyles from './styles/result_styles';

import useSearch from '../../hooks/use_search';
import { useSearchState, useSearchDispatch } from '../../hooks/use_search_state';

import Results from '../results/results';
import SearchControls from './search_controls';
import OnSaleOnly from './on_sale_only';
import SortBy from './sort_by';
import SearchSlider from './search_slider';
import SearchBox from './search_box';
import Filters from './filters';
import FilterControls, { AnimationDelay } from './filter_controls';
import ClearFiltersButton from './filters/clear_filters_button';

// Extracts all merchants from results and removes duplicates
function affiliateMerchants(unfilteredResults) {
  const merchants = {};
  for (let i = 0; i < unfilteredResults.length; i++) {
    merchants[unfilteredResults[i].lngMerchantId] = {
      id: unfilteredResults[i].lngMerchantId,
      name: unfilteredResults[i].strMerchantName,
    };
  }

  return Object.keys(merchants).map((k) => merchants[k]);
}

export default function Search(props) {
  const { settings } = props;
  const {
    forAffiliate = false,
    hideSort,
    queryParam,
    allowFiltering,
    showOnSaleOnly,
  } = settings;

  const {
    setPage,
    // setResultCount,
    setQuery,
    setMinPrice,
    setMaxPrice,
    setSortOrder,
    setOnSaleOnly,
  } = useSearchDispatch();

  const clearBtnClassName = 'snapgadget-btn';
  const queryRef = useRef();

  const [showFilters, setShowFilters] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const firstLoadStyle = firstLoad ? { display: 'none' } : {};
  const searchArgs = useSearchState();

  const {
    query,
    sortOrder,
    onSaleOnly,
    page,
  } = searchArgs;

  const {
    unfilteredResults, results, loading, hasSearched, hasMore,
  } = useSearch(searchArgs, false, allowFiltering);

  const [showResults, setShowResults] = useState(true);

  const showSlider = !!settings.slider;

  useEffect(() => {
    if (queryParam) {
      // Change the url query param
      updateQueryStringParam(queryParam, query);
    }
  }, [query, queryParam]);

  const filterControls = allowFiltering && (
    <FilterControls
      firstLoadStyle={firstLoadStyle}
      setFirstLoad={setFirstLoad}
      showResults={showResults}
      setShowResults={setShowResults}
      showFilters={showFilters}
      setShowFilters={(show) => {
        if (show === false) {
          queryRef.current?.focus();
        }
        setShowFilters(show);
      }}
    />
  );

  return (
    <form className="snapgadget_main_container" onSubmit={(e) => e.preventDefault()}>
      <SearchStyles settings={settings} />
      <ResultStyles settings={settings} />
      <div className="snapgadget_controls">
        <div className="snapgadget_controls_row">
          <SearchBox
            hideSearch={settings.hideSearch}
            query={query}
            setQuery={setQuery}
            placeholder={settings.placeholder || ''}
            queryRef={queryRef}
          />
          <SearchSlider
            showSlider={showSlider}
            minRangePrice={settings.searchPriceMinimum || 0}
            setMinPrice={setMinPrice}
            maxRangePrice={settings.searchPriceMaximum || 10000}
            setMaxPrice={setMaxPrice}
            settings={settings}
          />
          <div className="snapgadget_controls_sub_row">
            <SortBy
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              forAffiliate={forAffiliate}
              hideSort={hideSort}
            />
            {filterControls}
          </div>
        </div>
        <div className="snapgadget_controls_row_reverse">
          {
            !hideSort && showOnSaleOnly && showFilters
              ? (
                <ClearFiltersButton
                  clearBtnClassName={clearBtnClassName}
                />
              )
              : null
          }
          <OnSaleOnly
            settings={settings}
            onSaleOnly={onSaleOnly}
            onChange={(checked) => setOnSaleOnly(checked)}
          />
        </div>
      </div>
      {
        showFilters
          && (
            <CSSTransition
              in={showFilters}
              timeout={AnimationDelay}
              classNames="snapgadget-fade"
            >
              <div style={firstLoadStyle}>
                <Filters
                  unfilteredResults={unfilteredResults}
                  results={results}
                  loading={loading}
                  affiliateMerchants={allowFiltering ? affiliateMerchants(unfilteredResults) : []}
                  clearBtnClassName={clearBtnClassName}
                  settings={settings}
                  hideSort={!hideSort}
                  hideOnSaleOnly={showOnSaleOnly}
                />
              </div>
            </CSSTransition>
          )
      }
      <Results
        allResults={results}
        settings={settings}
      />
      <SearchControls
        settings={settings}
        results={results}
        loadingSearch={loading}
        hasSearched={hasSearched}
        getMoreResults={() => setPage(page + 1)}
        hasMore={hasMore}
      />
    </form>
  );
}

Search.propTypes = {
  settings: PropTypes.shape({
    forAffiliate: PropTypes.bool,
    hideSort: PropTypes.bool,
    queryParam: PropTypes.string,
    searchPriceMinimum: PropTypes.number,
    searchPriceMaximum: PropTypes.number,
    hideSearch: PropTypes.bool,
    placeholder: PropTypes.string,
    slider: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
    ]),
    allowFiltering: PropTypes.bool,
    showOnSaleOnly: PropTypes.bool,
  }).isRequired,
};
